import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { useTheme, THEME_LABELS } from '../../contexts/ThemeContext';
import { useChat } from '../../contexts/ChatContext';
import { CacheService } from '../../utils/cache';
import { cn } from '../ui/cn';
import {
  searchEntries,
  buildPath,
  filterAndSortEntries,
  normalizeSearch,
  SEARCH_SECTION_LABELS,
  type SearchEntry,
} from '../../configs/searchCatalog';

interface Command {
  id: string;
  label: string;
  group: string;
  icon?: React.ReactNode;
  hint?: string;
  run: () => void;
}

const THEME_KEYWORDS = ['tema', 'modo', 'color', 'oscuro', 'claro', 'oficina', 'contraste', 'colorido', 'theme', 'apariencia'];

// El asistente es una ACCIÓN (abrir el chat), no una página: se muestra como
// comando aparte, visible cuando no hay query o cuando ésta lo nombra
// (incluye cómo lo nombra la gente: chat, bot, aichatbot, la vaca...).
const CHAT_KEYWORDS = [
  'chat', 'asistente', 'ia', 'aichatbot', 'bot', 'vaca', 'pregunta', 'preguntar',
  'ayuda', 'soporte', 'atencion', 'atención', 'conversar', 'mensaje', 'gaucho',
];

/**
 * Command palette (⌘K / Ctrl+K). Buscador global de navegación y acciones,
 * con catálogo exhaustivo (`searchCatalog`), match por sinónimos/keywords y
 * navegación por teclado (↑↓ enter esc). Se monta en el Layout (solo autenticado).
 */
const CommandPalette: React.FC = () => {
  const navigate = useNavigate();
  const { can, hasRole } = usePermissions();
  const { theme, cycle } = useTheme();
  const { openChat } = useChat();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Abrir/cerrar con ⌘K / Ctrl+K, o vía evento custom (botón del header).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const onCustom = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('mcd:open-command-palette', onCustom);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mcd:open-command-palette', onCustom);
    };
  }, []);

  // Reset al abrir y enfocar el input.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  const go = (path: string) => {
    if (path === '/animals/new') {
      CacheService.remove('campoDefault');
      CacheService.remove('lastFechaEvento');
    }
    navigate(path);
    setOpen(false);
  };

  // Catálogo filtrado por permisos + query, ordenado por sección y score.
  const filtered = useMemo(
    () => filterAndSortEntries(searchEntries, query, can, hasRole),
    [query, can, hasRole],
  );

  // Comando de cambio de tema (acción, no navegación): visible si no hay query
  // o si el query matchea palabras relacionadas con tema/color.
  const themeVisible = useMemo(() => {
    const q = normalizeSearch(query);
    if (!q) return true;
    return THEME_KEYWORDS.some((k) => k.includes(q) || q.includes(k));
  }, [query]);

  const themeCmd = useMemo<Command>(
    () => ({
      id: 'theme-cycle',
      label: `Cambiar modo de color (actual: ${THEME_LABELS[theme]})`,
      group: 'Preferencias',
      icon: (
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none" />
        </svg>
      ),
      hint: 'Oficina · Colorido · Oscuro',
      run: () => {
        cycle();
        setOpen(false);
      },
    }),
    [theme, cycle],
  );

  const chatVisible = useMemo(() => {
    const q = normalizeSearch(query);
    if (!q) return true;
    return CHAT_KEYWORDS.some((k) => k.includes(q) || q.includes(k));
  }, [query]);

  const chatCmd = useMemo<Command>(
    () => ({
      id: 'open-chat',
      label: 'Abrir el asistente de IA',
      group: 'Asistente',
      icon: <span className="text-base" aria-hidden="true">🐄</span>,
      hint: 'Preguntale lo que quieras — hasta por voz',
      run: () => {
        openChat();
        setOpen(false);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openChat],
  );

  // Lista final de comandos: entradas del catálogo + tema + asistente.
  const commands = useMemo<Command[]>(() => {
    const fromEntries: Command[] = filtered.map((e: SearchEntry) => ({
      id: e.id,
      label: e.label,
      group: SEARCH_SECTION_LABELS[e.section],
      icon: e.icon,
      run: () => go(buildPath(e)),
    }));
    const extras = [themeVisible ? themeCmd : null, chatVisible ? chatCmd : null].filter(Boolean);
    return [...fromEntries, ...(extras as Command[])];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, themeCmd, themeVisible, chatCmd, chatVisible]);

  // Mantener `active` dentro del rango.
  useEffect(() => {
    if (active >= commands.length) setActive(0);
  }, [commands, active]);

  // Scroll del item activo a la vista.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cmd-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (commands.length ? (i + 1) % commands.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (commands.length ? (i - 1 + commands.length) % commands.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commands[active]?.run();
    }
  };

  if (!open) return null;

  // Agrupar manteniendo el orden de la lista (los grupos ya quedan contiguos).
  let lastGroup = '';

  return createPortal(
    <div className="fixed inset-0 z-[60] flex justify-center" role="dialog" aria-modal="true" aria-label="Búsqueda de comandos">
      <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden="true" />
      <div className="relative z-10 mt-[10vh] h-fit w-full max-w-xl overflow-hidden rounded-lg border border-hairline bg-paper shadow-overlay">
        {/* Buscador */}
        <div className="flex items-center gap-3 border-b border-hairline px-4">
          <svg className="h-4 w-4 shrink-0 text-sage" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Buscar sección o acción…"
            className="w-full bg-transparent py-3 text-sm text-ink placeholder:text-sage/70 focus:outline-none"
          />
          <kbd className="shrink-0 rounded border border-hairline bg-paper-2 px-1.5 py-0.5 text-[10px] text-sage">esc</kbd>
        </div>

        {/* Lista */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto p-2">
          {commands.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-sage">No hay resultados para “{query}”.</div>
          ) : (
            commands.map((c, i) => {
              const showGroup = c.group !== lastGroup;
              lastGroup = c.group;
              return (
                <React.Fragment key={c.id}>
                  {showGroup && (
                    <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-sage/70">{c.group}</div>
                  )}
                  <button
                    type="button"
                    data-cmd-idx={i}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => c.run()}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
                      i === active ? 'bg-forest/10 text-ink' : 'text-ink hover:bg-paper-2',
                    )}
                  >
                    <span className={cn('flex h-5 w-5 items-center justify-center', i === active ? 'text-forest' : 'text-sage')}>{c.icon}</span>
                    <span className="flex-1 truncate">{c.label}</span>
                    {c.hint && <span className="text-xs text-sage">{c.hint}</span>}
                  </button>
                </React.Fragment>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 border-t border-hairline bg-paper-2/50 px-4 py-2 text-xs text-sage">
          <span>↑↓ navegar</span>
          <span>↵ abrir</span>
          <span>esc cerrar</span>
          <span className="ml-auto inline-flex items-center gap-1">
            <kbd className="rounded border border-hairline bg-paper px-1.5 py-0.5 font-sans text-[10px]">⌘K</kbd>
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CommandPalette;
